import {
  AuthErrorField,
  AuthErrorKind,
  ChangeEmailErrorKind,
  EmailChangeRequestOutcome,
} from '@griply/types';

import { mapAuthError } from './map-auth-error';

const KIND_BY_OUTCOME: Record<
  EmailChangeRequestOutcome,
  ChangeEmailErrorKind | null
> = {
  [EmailChangeRequestOutcome.Sent]: null,
  [EmailChangeRequestOutcome.InvalidEmail]: ChangeEmailErrorKind.InvalidEmail,
  [EmailChangeRequestOutcome.SameEmail]: ChangeEmailErrorKind.SameEmail,
  [EmailChangeRequestOutcome.EmailTaken]: ChangeEmailErrorKind.EmailTaken,
  [EmailChangeRequestOutcome.RateLimited]: ChangeEmailErrorKind.TooManyRequests,
  [EmailChangeRequestOutcome.NotRecentSignIn]: ChangeEmailErrorKind.Unknown,
  [EmailChangeRequestOutcome.NoPassword]: ChangeEmailErrorKind.Unknown,
};

const isRequestOutcome = (
  outcome: unknown,
): outcome is EmailChangeRequestOutcome =>
  typeof outcome === 'string' && outcome in KIND_BY_OUTCOME;

// Null when the request was sent; the outcome arrives off the wire, so a value this version does not know is unknown.
export const getChangeEmailErrorKind = (
  outcome: unknown,
): ChangeEmailErrorKind | null =>
  isRequestOutcome(outcome)
    ? KIND_BY_OUTCOME[outcome]
    : ChangeEmailErrorKind.Unknown;

const MESSAGE_KEY_BY_KIND: Record<ChangeEmailErrorKind, string> = {
  [ChangeEmailErrorKind.WrongPassword]:
    'auth.change-email.errors.wrong-password',
  [ChangeEmailErrorKind.InvalidEmail]: 'login.invalid-email',
  [ChangeEmailErrorKind.SameEmail]: 'auth.change-email.errors.same-email',
  [ChangeEmailErrorKind.EmailTaken]: 'auth.change-email.errors.email-taken',
  [ChangeEmailErrorKind.TooManyRequests]: 'auth.errors.too-many-requests',
  [ChangeEmailErrorKind.Network]: 'auth.errors.network',
  [ChangeEmailErrorKind.Unknown]: 'auth.errors.unknown',
};

export const getChangeEmailErrorMessageKey = (
  kind: ChangeEmailErrorKind,
): string => MESSAGE_KEY_BY_KIND[kind];

const FIELD_BY_KIND = new Map<ChangeEmailErrorKind, AuthErrorField>([
  [ChangeEmailErrorKind.WrongPassword, AuthErrorField.Password],
  [ChangeEmailErrorKind.InvalidEmail, AuthErrorField.Email],
  [ChangeEmailErrorKind.SameEmail, AuthErrorField.Email],
  [ChangeEmailErrorKind.EmailTaken, AuthErrorField.Email],
]);

export const getChangeEmailErrorField = (
  kind: ChangeEmailErrorKind,
): AuthErrorField => FIELD_BY_KIND.get(kind) ?? AuthErrorField.Form;

const KIND_BY_AUTH_KIND = new Map<AuthErrorKind, ChangeEmailErrorKind>([
  [AuthErrorKind.WrongCombination, ChangeEmailErrorKind.WrongPassword],
  [AuthErrorKind.TooManyRequests, ChangeEmailErrorKind.TooManyRequests],
  [AuthErrorKind.Network, ChangeEmailErrorKind.Network],
]);

// A thrown failure of the reauthentication or the callable; Unknown is the caller's to report.
export const getChangeEmailFailureKind = (
  error: unknown,
): ChangeEmailErrorKind =>
  KIND_BY_AUTH_KIND.get(mapAuthError(error)) ?? ChangeEmailErrorKind.Unknown;
